<template>
    <button class="button" 
        :class="buttonClass" 
        :type="type" 
        :disabled="isDisabled"
        v-bind="$attrs">
        <span v-if="text.length" class="button__text">
            {{ text }}
        </span>
        <SvgIcon v-if="iconName?.length" 
            class="button__icon" 
            :class="iconPlaceClass"
            :name="iconName" 
            :size="iconSize" />
    </button>
</template>

<script>
import SvgIcon from '@/components/ui/SvgIcon.vue';

const THEMES = ['primary', 'primary-invert', 'muted'];
const SIZES = ['small', 'medium', 'big', 'large'];
const ICON_PLACES = ['left', 'right'];
const ICON_SIZES = ['small', 'base', 'medium', 'big', 'huge'];

export default {
    name: 'BaseButton',
    components: {
        SvgIcon
    },
    props: {
        type: {
            type: String,
            required: false,
            default: 'button'
        },
        text: {
            type: String,
            required: false,
            default: ''
        },
        isDisabled: {
            type: Boolean,
            required: false,
            default: false
        },
        theme: {
            type: String,
            required: false,
            default: 'primary',
            validator: function(theme) {
                return theme ? THEMES.includes(theme) : true
            }
        },
        size: {
            type: String,
            required: false,
            default: 'medium',
            validator: function(size) {
                return size ? SIZES.includes(size) : true
            }
        },
        iconName: {
            type: String,
            required: false,
            default: ''
        },
        iconPlace: {
            type: String,
            required: false,
            default: 'right',
            validator: function(place) {
                return ICON_PLACES.includes(place)
            }
        },
        iconSize: {
            type: String,
            required: false,
            default: 'base',
            validator: function(size) {
                return size ? ICON_SIZES.includes(size) : true
            }
        },
        long: {
            type: Boolean,
            required: false,
            deafult: false
        },
        isText: {
            type: Boolean,
            required: false,
            default: false
        },
        isIconed: {
            type: Boolean,
            required: false,
            default: false
        },
        underlineText: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    computed: {
        themeClass() {
            return `button--theme-${this.theme}`
        },
        sizeClass() {
            return `button--size-${this.size}`
        },
        lengthClass() {
            return this.long ? `button--long` : '';
        },
        isTextClass() {
            return this.isText ? 'button--text' : ''
        },
        underlineClass() {
            return this.underlineText ? 'button--underline-text' : ''
        },
        iconedClass() {
            return this.isIconed ? 'button--iconed' : ''
        },
        iconPlaceClass() {
            return this.isIconed ? '' : `button__icon--${this.iconPlace}`
        },
        buttonClass() {
            return `${this.iconedClass} ${this.themeClass} ${this.sizeClass} ${this.lengthClass} ${this.isTextClass} ${this.underlineClass}`
        },
    }
}
</script>

<style scoped lang="scss">
.button {
    $root: &;

    $height-medium: 42px;
    $height-big: 46px;
    $height-large: 50px;

    $color-bg-default: $primary;

    @include text-medium($font-weight-medium, $line-height-normal);

    position: relative;

    display: inline-flex;
    align-items: center;
    justify-content: center;

    appearance: none;
    -webkit-appearance: none;

    cursor: pointer;
    transition: all $transition-duration $transition-function;
    text-decoration: none;

    border-radius: $border-radius-small;
    
    @media #{$screen-big} {
        font-size: rem($font-size-normal);
    }

    @media #{$screen-small} {
        font-size: rem($font-size-small);
    }  
    
    &:disabled {
        cursor: default;
    }

    // sizes

    &--size-medium {
        height: rem($height-medium);
        padding: 0 rem($gap-medium);
    }

    &--size-big {
        height: rem($height-big);
        padding: 0 rem($gap-big);
    }

    &--size-large {
        height: rem($height-large);
        padding: 0 rem($gap-big);
    }

    &--long {
        width: 100%;
    }
    // themes

    &--theme-primary {
        border: 1px solid $primary;
        color: $primary-invert;
        background: $primary;

        &:hover, &:focus {
            border: 1px solid $primary;
            color: $primary;
            background: $primary-invert;
        }

        &:disabled {
            cursor: not-allowed;

            color: $primary-invert;
            border-color: $color-button-background-disabled;
            background-color: $color-button-background-disabled;
        }
    }

    &--theme-primary-invert {
        border: 1px solid $primary;
        color: $primary;
        background: $primary-invert;

        &:hover, &:focus {
            border: 1px solid $primary;
            color: $primary-invert;
            background: $primary;
        }
    }

        // types
    &--text {
        padding-left: 0;
        padding-right: 0;

        border-radius: 0;
    }

    &--text#{&}--theme-muted {
        border: none;
        color: $color-text-muted;
        background-color: transparent;
    }

    &--text#{&}--theme-primary {
        border: none;
        color: $primary;
        background-color: transparent;

        &:hover, &:focus {
            border: none;
            color: $primary;
            background-color: transparent;
        }

        &:disabled {
            color: $color-button-background-disabled;
            background-color: transparent;
        }
    }
    
    &--text#{&}--size-small {
        @include text-normal($font-weight-regular, $line-height-normal);

        @media #{$screen-small} {
            @include text-small($font-weight-regular, $line-height-huge);
        } 
        
    }

    &--iconed {
        width: auto;
        height: auto;
        padding: 0;

        background-color: transparent;
        border: none;

        &:hover, &:focus {
            border: none;
            background-color: transparent;
        }
    }

    &--iconed#{&}--theme-primary {
        color: $primary;
    }

    // decorations

    &--underline-text {
        text-decoration: underline;
    }
    

    &__text {
        @include text-ellipsis;

        color: inherit;
    }

    &__icon {
        $icon-margin: $gap-normal;

        &--right {
            order: 1;

            margin-right: 0;
            margin-left: rem($icon-margin);
        }

        &--left {
            order: -1;

            margin-right: rem($icon-margin);
            margin-left: 0;
        }
    }

}
</style>